import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  OPP_ENUM_FILTER,
  OPPORTUNITY_CLIENT_EVENT_ACTION_FIELD,
  OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE,
  TOpportunitiesV3ListItem,
  TOpportunitiesV3Summary,
  buildOpportunityClientEventFilters,
  isOpportunityClientEventFilterField,
  mapOpportunityTypeToEventTargetKind,
  trackOpportunityClientEvent,
  useGetOpportunitiesV3ListInfiniteQuery,
  useGetOpportunitiesV3SummaryQuery
} from 'integration/resources/opportunitiesV3'
import useDebounce from 'src/hooks/useDebounce'
import { useIsGoodsAndServices } from 'src/hooks/useIsGoodsAndServices'
import {
  defaultFilters,
  useOpportunitiesV3FiltersAtom
} from 'src/store/screens/opportunitiesV3Filters'
import { formatOpportunitiesProcessedAt } from 'src/utils/date'
import { calculateActiveFilterCount } from 'src/utils/filters'
import { LogEvent } from 'src/utils/logEvents'

import { UseOpportunitiesListScreen } from './OpportunitiesListScreen.types'

export const useOpportunitiesListScreen: UseOpportunitiesListScreen = ({ navigation }) => {
  const [prioritySelected, setPrioritySelected] = useState<string | undefined>(undefined)

  const lastTrackedSearchRef = useRef<string | undefined>(undefined)

  const canTrackPageChangeRef = useRef(false)

  const [filtersState, setFiltersState] = useOpportunitiesV3FiltersAtom()

  const isGoodsAndServices = useIsGoodsAndServices()

  const [search, setSearch] = useState('')

  const debouncedSearch = useDebounce(search, 500)

  const opportunityKinds = useMemo(() => {
    const kinds = [
      { label: 'Oportunidades', value: OPP_ENUM_FILTER.OPPORTUNITY },
      {
        label: isGoodsAndServices ? 'Base Ativa' : 'Prospecção',
        value: OPP_ENUM_FILTER.ACTIVE_BASE
      },
      { label: 'Cobrança', value: OPP_ENUM_FILTER.CHARGES },
      { label: 'APNEF', value: OPP_ENUM_FILTER.APNEF }
    ]

    if (!isGoodsAndServices) {
      kinds.push({ label: 'Parcer.IA', value: OPP_ENUM_FILTER.PARTNER_IA })
    }

    return kinds
  }, [isGoodsAndServices])

  useEffect(() => {
    if (!isGoodsAndServices) return

    if (filtersState.kind !== OPP_ENUM_FILTER.PARTNER_IA) return

    setFiltersState((current) => ({
      ...current,
      kind: OPP_ENUM_FILTER.OPPORTUNITY
    }))
  }, [filtersState.kind, isGoodsAndServices, setFiltersState])

  const summaryParams = useMemo(
    () => ({
      priority: filtersState.priority,
      public_rating: filtersState.public_rating,
      fidelity: filtersState.fidelity,
      segment: filtersState.segment,
      network: filtersState.network,
      subsidiary: filtersState.subsidiary,
      user_std_code: filtersState.user_std_code
    }),
    [filtersState]
  )

  const { data: opportunitiesHeaderData, isLoading: isLoadingOpportunitiesHeader } =
    useGetOpportunitiesV3SummaryQuery(summaryParams)

  const opportunitiesHeader: TOpportunitiesV3Summary | undefined = useMemo(() => {
    const body = opportunitiesHeaderData?.data

    if (!body) return undefined

    return {
      year_month: body.year_month,
      priorities: body.priorities ?? []
    }
  }, [opportunitiesHeaderData])

  const {
    data: opportunitiesListData,
    isLoading: isLoadingOpportunitiesList,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchOpportunitiesList
  } = useGetOpportunitiesV3ListInfiniteQuery({
    per_page: 10,
    ...(debouncedSearch?.length ? { search: debouncedSearch } : {}),
    kind: filtersState.kind,
    priority: filtersState.priority,
    public_rating: filtersState.public_rating,
    fidelity: filtersState.fidelity,
    segment: filtersState.segment,
    network: filtersState.network,
    subsidiary: filtersState.subsidiary,
    user_std_code: filtersState.user_std_code,
    order_by: filtersState.order_by,
    order_by_direction: filtersState.order_by_direction
  })

  const opportunitiesList = useMemo(
    () =>
      opportunitiesListData?.pages.reduce<TOpportunitiesV3ListItem[]>(
        (previousValue, currentValue) => [...previousValue, ...(currentValue.data.data || [])],
        []
      ) ?? [],
    [opportunitiesListData]
  )

  const totalItems = opportunitiesListData?.pages[0]?.data.metadata.pagination.total ?? 0

  const processedAtLabel = useMemo(() => {
    const processedAt = opportunitiesListData?.pages[0]?.data.processed_at

    return formatOpportunitiesProcessedAt(processedAt)
  }, [opportunitiesListData])

  const buildSnapshot = useCallback(
    (overrides?: {
      kind?: OPP_ENUM_FILTER
      search?: string
      priority?: string[]
      public_rating?: string[]
      fidelity?: string[]
      segment?: string[]
      network?: string[]
      subsidiary?: string[]
      user_std_code?: string[]
    }) =>
      buildOpportunityClientEventFilters({
        yearMonth: opportunitiesHeader?.year_month,
        kind: overrides && 'kind' in overrides ? overrides.kind : filtersState.kind,
        search: overrides?.search ?? debouncedSearch,
        priority: overrides?.priority ?? filtersState.priority,
        public_rating: overrides?.public_rating ?? filtersState.public_rating,
        fidelity: overrides?.fidelity ?? filtersState.fidelity,
        segment: overrides?.segment ?? filtersState.segment,
        network: overrides?.network ?? filtersState.network,
        subsidiary: overrides?.subsidiary ?? filtersState.subsidiary,
        user_std_code: overrides?.user_std_code ?? filtersState.user_std_code
      }),
    [debouncedSearch, filtersState, opportunitiesHeader?.year_month]
  )

  useEffect(() => {
    canTrackPageChangeRef.current = false
  }, [
    debouncedSearch,
    filtersState.kind,
    filtersState.priority,
    filtersState.public_rating,
    filtersState.fidelity,
    filtersState.segment,
    filtersState.network,
    filtersState.subsidiary,
    filtersState.user_std_code
  ])

  useEffect(() => {
    if (lastTrackedSearchRef.current === undefined) {
      lastTrackedSearchRef.current = debouncedSearch

      return
    }

    if (lastTrackedSearchRef.current === debouncedSearch) {
      return
    }

    lastTrackedSearchRef.current = debouncedSearch

    trackOpportunityClientEvent({
      action: {
        type: OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE.SEARCH_CHANGED,
        field: OPPORTUNITY_CLIENT_EVENT_ACTION_FIELD.SEARCH,
        value: debouncedSearch
      },
      filters: buildSnapshot({ search: debouncedSearch }),
      target: null
    })
  }, [buildSnapshot, debouncedSearch])

  const handleListScroll = useCallback((offsetY: number) => {
    if (offsetY > 16) {
      canTrackPageChangeRef.current = true
    }
  }, [])

  const fetchMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      const nextPage = (opportunitiesListData?.pages.length ?? 0) + 1

      fetchNextPage()

      if (!canTrackPageChangeRef.current) {
        return
      }

      trackOpportunityClientEvent({
        action: {
          type: OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE.PAGE_CHANGED,
          field: OPPORTUNITY_CLIENT_EVENT_ACTION_FIELD.PAGE,
          value: nextPage
        },
        filters: buildSnapshot(),
        target: null
      })
    }
  }, [
    buildSnapshot,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    opportunitiesListData?.pages.length
  ])

  const handleSelectOpportunityKind = (kind: OPP_ENUM_FILTER) => {
    const nextKind = filtersState.kind === kind ? undefined : kind

    setFiltersState({
      ...filtersState,
      kind: nextKind
    })

    trackOpportunityClientEvent({
      action: {
        type: OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE.KIND_CHANGED,
        field: OPPORTUNITY_CLIENT_EVENT_ACTION_FIELD.KIND,
        value: kind
      },
      filters: buildSnapshot({ kind: nextKind }),
      target: null
    })
  }

  const handleSelectPriority = (priority: string) => {
    const newPriority = priority === prioritySelected ? undefined : priority

    const nextPriority = newPriority ? [newPriority] : []

    setPrioritySelected(newPriority)

    setFiltersState({
      ...filtersState,
      priority: nextPriority
    })

    trackOpportunityClientEvent({
      action: {
        type: newPriority
          ? OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE.FILTER_SELECTED
          : OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE.FILTER_DESELECTED,
        field: OPPORTUNITY_CLIENT_EVENT_ACTION_FIELD.PRIORITY,
        value: priority
      },
      filters: buildSnapshot({ priority: nextPriority }),
      target: null
    })
  }

  const modalFilters = useMemo(
    () => ({
      priority: filtersState.priority,
      public_rating: filtersState.public_rating,
      fidelity: filtersState.fidelity,
      segment: filtersState.segment,
      network: filtersState.network,
      subsidiary: filtersState.subsidiary,
      user_std_code: filtersState.user_std_code
    }),
    [filtersState]
  )

  const activeFilterCount = calculateActiveFilterCount(modalFilters)

  const hasFilter = activeFilterCount > 0

  const handleGoToFilters = () => {
    LogEvent(`oportunidades_odc_acessar_filtros`, {
      item_id: ''
    })

    navigation.navigate('OpportunitiesV3Filter')
  }

  const handleGoToDetails = (item: TOpportunitiesV3ListItem) => {
    trackOpportunityClientEvent({
      action: { type: OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE.OPPORTUNITY_OPENED },
      filters: buildSnapshot(),
      target: {
        opportunity_id: item.opportunity_id,
        kind: mapOpportunityTypeToEventTargetKind(item.type)
      }
    })

    navigation.navigate('OpportunitiesDetails', {
      id: item.opportunity_id,
      type: item.type
    })
  }

  const handleRemoveFilter = (key: string, raw?: string) => {
    const current = modalFilters[key as keyof typeof modalFilters]

    if (!Array.isArray(current)) {
      setFiltersState({ ...filtersState, [key]: undefined })

      return
    }

    const next = (current as (string | number)[]).filter(
      (value: string | number) => String(value) !== raw
    )

    setFiltersState({ ...filtersState, [key]: next })

    if (key === 'priority') {
      setPrioritySelected(undefined)
    }

    if (!isOpportunityClientEventFilterField(key) || raw === undefined) {
      return
    }

    trackOpportunityClientEvent({
      action: {
        type: OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE.FILTER_DESELECTED,
        field: key,
        value: raw
      },
      filters: buildSnapshot({ [key]: next }),
      target: null
    })
  }

  const handleResetFilters = () => {
    setFiltersState(defaultFilters)

    trackOpportunityClientEvent({
      action: { type: OPPORTUNITY_CLIENT_EVENT_ACTION_TYPE.FILTERS_RESET },
      filters: buildSnapshot({
        kind: undefined,
        priority: [],
        public_rating: [],
        fidelity: [],
        segment: [],
        network: [],
        subsidiary: [],
        user_std_code: []
      }),
      target: null
    })
  }

  return {
    prioritySelected,
    handleSelectPriority,
    opportunitiesHeader,
    isLoadingOpportunitiesHeader,
    processedAtLabel,
    search,
    setSearch,
    opportunityKinds,
    selectedOpportunityKind: filtersState.kind,
    handleSelectOpportunityKind,
    handleGoToFilters,
    activeFilterCount,
    hasFilter,
    activeFilters: modalFilters,
    handleRemoveFilter,
    handleResetFilters,
    handleGoToDetails,
    opportunitiesList,
    totalItems,
    isLoadingOpportunitiesList,
    isFetchingNextPage,
    fetchMore,
    handleListScroll,
    refetchOpportunitiesList
  }
}
